/**
 * Vue Router
 *
 * @library
 *
 * https://router.vuejs.org/en/
 */

// Lib imports
import Vue from 'vue'
import VueAnalytics from 'vue-analytics'
import Router from 'vue-router'
import { TokenService } from '../services/storage.service'
import Store from '../store/index'

// Routes
import paths from './paths'
import { captureGroupLink } from '../services/group.service'

captureGroupLink()

function route (path, view, name, meta, redirect) {
  if (redirect) {
    return { path, redirect }
  }
  return {
    name: name || view,
    path,
    meta,
    component: (resolve) => import(
      `@/views/${view}.vue`
    ).then(resolve)
  }
}

// Receives number of weeks n and returns the date in n weeks as a string 'YYYY-MM-DD'
function getFutureDay (weeks) {
  var date = new Date()
  date.setDate(date.getDate() + 7 * weeks)
  var futureDate = date.toISOString().split('T')[0]
  return futureDate
}

function setFutureDateFromUrl (to, Store) {
  const todaysDate = new Date().toISOString().split('T')[0]
  const defaultDateEnd = getFutureDay(4)
  const defaultDateFrom = todaysDate

  if (to.query.date_sch_start) {
    if (to.query.date_sch_start.indexOf('/')) {
      const [month, day, year] = to.query.date_sch_start.split('/')
      to.query.date_sch_start = `${year}-${month}-${day}`
    }

    Store.commit('setScheduleDateFrom', to.query.date_sch_start)
  } else {
    Store.commit('setScheduleDateFrom', defaultDateFrom)
  }

  if (to.query.date_sch_end) {
    if (to.query.date_sch_end.indexOf('/')) {
      const [month, day, year] = to.query.date_sch_end.split('/')
      to.query.date_sch_end = `${year}-${month}-${day}`
    }

    Store.commit('setScheduleDateTo', to.query.date_sch_end)
  } else {
    Store.commit('setScheduleDateTo', defaultDateEnd)
  }
}

// Default date range is YTD for clients and retailers
function setDateFromUrl (to, Store) {
  const d = new Date()
  const todaysDate = d.toISOString().split('T')[0]
  const firstDayOfYear = new Date(d.getFullYear(), 0, 1).toISOString().split('T')[0]
  let defaultDateFrom = firstDayOfYear
  let defaultDateEnd = todaysDate

  if (to.query.date_start) {
    if (to.query.date_start.indexOf('/') > -1) {
      const [month, day, year] = to.query.date_start.split('/')
      to.query.date_start = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
    // Use query param if it exists
    defaultDateFrom = to.query.date_start
  }

  Store.commit('setDateFrom', defaultDateFrom)

  if (to.query.date_end) {
    if (to.query.date_end.indexOf('/') > -1) {
      const [month, day, year] = to.query.date_end.split('/')
      to.query.date_end = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
    // Use query param if it exists
    defaultDateEnd = to.query.date_end
  }
  Store.commit('setDateTo', defaultDateEnd)
}

function setStateFromUrl (to, Store) {
  if (to.query.state) {
    Store.commit('setStateFilter', to.query.state)
  }
}

Vue.use(Router)

// Create a new router
const router = new Router({
  mode: 'history',
  routes: paths.map(path => route(path.path, path.view, path.name, path.meta, path.redirect)).concat([
    { path: '*', redirect: '/welcome' }
  ]),
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { selector: to.hash }
    }
    return { x: 0, y: 0 }
  }
})

function safeInternalPath (path, fallback) {
  return typeof path === 'string' && path.charAt(0) === '/' && path.substring(0, 2) !== '//'
    ? path
    : fallback
}

function canonicalDashboardPath (to, fallback) {
  const allowedPaths = ['/analytics', '/calendar', '/schedule', '/events', '/media', '/event-request', '/item-list']
  const segments = to.path.split('/').filter(Boolean)
  const candidate = segments.length ? '/' + segments[segments.length - 1] : fallback
  return allowedPaths.indexOf(candidate) !== -1 ? candidate : fallback
}

router.beforeEach((to, from, next) => {
  if (captureGroupLink({ pathname: to.path, hash: to.hash }, false)) {
    return next({ path: to.path, query: to.query, hash: '', replace: true })
  }
  const isPublic = to.matched.some((record) => { return record.meta.public === true })
  const doLogin = to.matched.some((record) => { return record.meta.dologin === true })
  const doRetailLogin = to.matched.some((record) => { return record.meta.doRetailLogin === true })
  const loggedIn = !!TokenService.getAccessKey() && !!TokenService.getLoginCode()
  const hasSeenLoginPage = TokenService.hasSeenLoginPage()

  // DataDog RUM action to track sessions by username
  if (loggedIn && window.DD_RUM) {
    const username = TokenService.getUsername()
    const loginCode = TokenService.getLoginCode()
    const loginType = localStorage.getItem('login_type')

    window.DD_RUM.addAction('user_login', {
      username: username,
      login_code: loginCode,
      login_type: loginType
    })
  }

  // CASE 1: Already on login page
  if (to.path === '/login') {
    // If logged in and already seen login page, redirect to analytics
    if (loggedIn && hasSeenLoginPage) {
      const requestedPath = safeInternalPath(to.query.redirect || to.query.return, null)
      return next(requestedPath || '/analytics')
    }
    // Otherwise stay on login page
    return next()
  }

  // CASE 3: Regular + retail login
  if (doLogin) {
    const requestedAccessKey = to.query.access_key || to.params.key
    const sameClientSession = loggedIn &&
      TokenService.getLoginType() === 'client' &&
      TokenService.getLoginCode() === to.params.client &&
      (!requestedAccessKey || TokenService.getAccessKey() === requestedAccessKey)
    if (!sameClientSession) {
      TokenService.clearLogin()
      Store.commit('setUserProfile', null)
    }

    setFutureDateFromUrl(to, Store)
    setStateFromUrl(to, Store)

    return Store.dispatch('set_client', {
      'access_key': sameClientSession ? TokenService.getAccessKey() : requestedAccessKey,
      'client_code': to.params.client
    })
      .then(function () {
        setDateFromUrl(to, Store)
        return next({path: canonicalDashboardPath(to, '/analytics'), replace: true})
      }).catch(function () {
        return next({
          path: '/login',
          query: {redirect: canonicalDashboardPath(to, '/analytics')}
        })
      })
  }

  if (doRetailLogin) {
    const sameRetailSession = loggedIn &&
      TokenService.getLoginType() === 'retail' &&
      TokenService.getLoginCode() === to.params.retail &&
      TokenService.getAccessKey() === to.params.retail_key
    if (!sameRetailSession) {
      TokenService.clearLogin()
      Store.commit('setUserProfile', null)
    }

    setFutureDateFromUrl(to, Store)
    setStateFromUrl(to, Store)

    return Store.dispatch('set_retail', {
      'retail_key': sameRetailSession ? TokenService.getAccessKey() : to.params.retail_key,
      'retail': to.params.retail
    })
      .then(function () {
        setDateFromUrl(to, Store)
        const requestedPath = canonicalDashboardPath(to, '/calendar')
        const retail = Store.state.retail
        const itemListEnabled = retail && (retail.show_item_list === 1 || retail.show_item_list === '1')
        const destination = requestedPath === '/item-list' && !itemListEnabled
          ? '/calendar'
          : requestedPath
        return next({path: destination, replace: true})
      }).catch(function () {
        return next({
          path: '/login',
          query: {redirect: canonicalDashboardPath(to, '/calendar')}
        })
      })
  }

  // CASE 4: Public login
  if (isPublic) {
    return next()
  }

  if (!loggedIn) {
    return next({
      path: '/login',
      query: {redirect: safeInternalPath(to.fullPath, '/analytics')}
    })
  }

  if (to.path === '/item-list') {
    const isRetail = TokenService.getLoginType() === 'retail'
    const itemListEnabled = TokenService.getItemListView() === 1 || TokenService.getItemListView() === '1'
    if (!isRetail || !itemListEnabled) {
      return next(isRetail ? '/calendar' : '/analytics')
    }
  }

  return next()
})

// Bootstrap Analytics
// Set in .env
// https://github.com/MatteoGabriele/vue-analytics
Vue.use(VueAnalytics, {
  id: 'UA-38117893-5',
  router,
  autoTracking: {
    page: process.env.NODE_ENV !== 'development'
  }
})

export default router
